import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useOrgId } from '@/lib/pulse/useEmployee';
import { mySurveyProgress, submitAnchorResponse, type SurveyAnchor } from '@/lib/pulse/surveyApi';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { toast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { Loader2, CheckCircle2 } from 'lucide-react';

function ScaleRow({
  anchor,
  pending,
  locked,
  onPick,
}: {
  anchor: SurveyAnchor;
  pending: boolean;
  locked: boolean;
  onPick: (v: number) => void;
}) {
  const max = 5;
  const labels = anchor.scale_labels ?? {};
  return (
    <div className="space-y-2">
      <div
        role="radiogroup"
        aria-label={anchor.name}
        className="grid gap-2 grid-cols-5"
      >
        {Array.from({ length: max }, (_, i) => i + 1).map((n) => {
          const selected = anchor.answered_value === n;
          const disabled = pending || locked;
          return (
            <button
              key={n}
              type="button"
              role="radio"
              aria-checked={selected}
              aria-disabled={disabled}
              disabled={disabled}
              onClick={() => onPick(n)}
              className={cn(
                'aspect-square rounded-lg border text-base font-medium transition-all',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                selected
                  ? 'bg-primary text-primary-foreground border-primary shadow-sm'
                  : 'bg-background border-border hover:border-foreground/30',
                disabled && 'opacity-60 cursor-not-allowed hover:border-border',
              )}
            >
              {n}
            </button>
          );
        })}
      </div>
      <div className="flex items-start justify-between gap-3 text-xs text-muted-foreground">
        <div className="flex flex-col items-start max-w-[45%]">
          <span className="font-semibold text-foreground">1 — {labels['1'] ?? 'Worst'}</span>
          <span>The lowest you've experienced on this.</span>
        </div>
        <div className="flex-1 mt-2 h-px bg-gradient-to-r from-destructive/40 via-muted-foreground/30 to-primary/60" />
        <div className="flex flex-col items-end text-right max-w-[45%]">
          <span className="font-semibold text-foreground">{max} — {labels[String(max)] ?? 'Best'}</span>
          <span>The best you've experienced on this.</span>
        </div>
      </div>
    </div>
  );
}

export default function SurveyTake() {
  const orgId = useOrgId();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [pendingId, setPendingId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['my_survey_progress', orgId],
    queryFn: async () => {
      const { data, error } = await mySurveyProgress(orgId!);
      if (error) throw error;
      return data;
    },
    enabled: !!orgId,
  });

  const { unanswered, answered } = useMemo(() => {
    if (!data || data.active !== true) return { unanswered: [], answered: [] };
    return {
      unanswered: data.anchors.filter((a) => a.answered_value === null),
      answered: data.anchors.filter((a) => a.answered_value !== null),
    };
  }, [data]);

  if (!orgId || isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!data || data.active !== true) {
    return (
      <div className="max-w-2xl mx-auto p-4 sm:p-8 space-y-4">
        <h1 className="text-2xl sm:text-3xl font-heading font-semibold">No active survey</h1>
        <p className="text-sm text-muted-foreground">
          There's nothing to answer right now. We'll let you know when there is.
        </p>
        <Button asChild variant="outline">
          <Link to="/app/employee">Back to Today</Link>
        </Button>
      </div>
    );
  }

  const submit = async (a: SurveyAnchor, v: number) => {
    if (!orgId || data.active !== true) return;
    setPendingId(a.id);
    const { error } = await submitAnchorResponse(orgId, data.survey_id, a.id, v);
    setPendingId(null);
    if (error) {
      toast({ title: 'Could not save', description: error.message, variant: 'destructive' });
      return;
    }
    qc.invalidateQueries({ queryKey: ['my_survey_progress', orgId] });
  };

  const pct = Math.round((data.answered / Math.max(1, data.total_anchors)) * 100);
  const done = data.answered >= data.total_anchors;

  return (
    <div className="max-w-2xl mx-auto p-4 sm:p-8 space-y-6">
      <header className="space-y-2">
        <Button asChild variant="ghost" size="sm" className="-ml-3 mb-1 h-auto px-2 py-1">
          <Link to="/app/employee">← Today</Link>
        </Button>
        <h1 className="text-2xl sm:text-3xl font-heading font-semibold">{data.survey_topic}</h1>
        <p className="text-sm text-muted-foreground">
          {done
            ? 'You\u2019re done. Change any answer below.'
            : `${data.answered} of ${data.total_anchors} answered.`}
        </p>
        <Progress value={pct} />
      </header>

      {unanswered.length > 0 && (
        <div className="space-y-4">
          {unanswered.map((a, idx) => (
            <Card key={a.id}>
              <CardHeader>
                <div className="flex items-start justify-between gap-2">
                  <CardTitle className="font-heading text-base">
                    {idx + 1 + answered.length}. {a.name}
                  </CardTitle>
                  {pendingId === a.id && (
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  )}
                </div>
                {a.description && <CardDescription>{a.description}</CardDescription>}
              </CardHeader>
              <CardContent>
                <ScaleRow anchor={a} pending={pendingId === a.id} locked={false} onPick={(v) => submit(a, v)} />
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {done && (
        <Card className="bg-muted/40 border-dashed">
          <CardContent className="flex items-start gap-3 pt-6">
            <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium">All answered. Thank you.</p>
              <p className="text-sm text-muted-foreground mt-0.5">
                Anything you want to add out loud? Charles is private.
              </p>
              <Button asChild variant="link" className="px-0 h-auto mt-1">
                <Link to="/app/employee/charles">Talk to Charles →</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {answered.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-sm font-medium text-muted-foreground">Already answered (locked)</h2>
          <p className="text-xs text-muted-foreground">
            Your answers are saved and can't be changed. If something needs editing, ask an admin to
            reopen the survey.
          </p>
          {answered.map((a, idx) => (
            <Card key={a.id} className="bg-muted/20">
              <CardHeader>
                <div className="flex items-start justify-between gap-2">
                  <CardTitle className="font-heading text-sm">
                    {idx + 1}. {a.name}
                  </CardTitle>
                  <Badge variant="secondary">You picked {a.answered_value}</Badge>
                </div>
              </CardHeader>
              <CardContent>
                <ScaleRow anchor={a} pending={false} locked onPick={() => {}} />
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
